import { z } from 'zod';
import { 
  AnalyticsEventData, 
  ContactEventData, 
  BatchEventData, 
  WebhookConfig 
} from '../schemas';

// 기본 이벤트 스키마
export const BaseEventSchema = z.object({
  event: z.string(),
  data: z.record(z.any()),
  userId: z.string().optional(),
  sessionId: z.string().optional(),
  timestamp: z.string().optional(),
});

// 분석 이벤트 스키마
export const AnalyticsEventSchema = BaseEventSchema.extend({
  event: z.string(),
  data: z.object({
    page: z.string().optional(),
    title: z.string().optional(),
    referrer: z.string().optional(),
    userAgent: z.string().optional(),
    button: z.string().optional(),
    action: z.string().optional(),
    category: z.string().optional(),
    value: z.number().optional(),
    element: z.string().optional(),
  }),
});

// 연락처 이벤트 스키마
export const ContactEventSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요'),
  email: z.string().email('올바른 이메일을 입력해주세요'),
  message: z.string().min(1, '메시지를 입력해주세요'),
  subject: z.string().optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  source: z.string().optional(),
});

// 배치 이벤트 스키마
export const BatchEventSchema = z.object({
  events: z.array(BaseEventSchema),
  batchId: z.string().optional(),
  source: z.string().optional(),
});

// 웹훅 설정 (기존 WebhookSettings와 구분)
export interface WebhookSettings {
  contactWebhookUrl: string;
  analyticsWebhookUrl: string;
  batchWebhookUrl: string;
  retryAttempts: number;
  retryDelay: number;
  timeout: number;
}

// 웹훅 응답
export interface WebhookResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
  timestamp: string;
}

// JSON 입력 폼 Props
export interface JsonInputFormProps {
  webhookType?: 'contact' | 'analytics' | 'batch';
  eventName?: string;
  onSubmit?: (data: any) => void;
  onSuccess?: (response: WebhookResponse) => void;
  onError?: (error: Error) => void;
  className?: string;
  submitButtonText?: string;
  loadingText?: string;
  placeholder?: string;
  defaultJson?: string;
}

// 연락처 폼 Props
export interface ContactFormProps {
  onSubmit?: (data: ContactEventData) => void;
  onSuccess?: (response: WebhookResponse) => void;
  onError?: (error: Error) => void;
  className?: string;
  submitButtonText?: string;
  loadingText?: string;
}

// 이벤트 트래커 Props
export interface EventTrackerProps {
  trackPageViews?: boolean;
  trackButtonClicks?: boolean;
  customEvents?: Array<{
    name: string;
    selector: string;
    event: string;
  }>;
  onEventTracked?: (eventData: AnalyticsEventData) => void;
}

// 웹훅 설정 컴포넌트 Props
export interface WebhookSettingsProps {
  onSettingsChange?: (settings: WebhookSettings) => void;
  className?: string;
}

// useWebhook 훅 옵션
export interface UseWebhookOptions {
  webhookType?: 'contact' | 'analytics' | 'batch';
  onSuccess?: (response: WebhookResponse) => void;
  onError?: (error: Error) => void;
  retryAttempts?: number;
  retryDelay?: number;
}

// useWebhook 훅 반환값
export interface UseWebhookReturn {
  sendAnalyticsEvent: (data: AnalyticsEventData) => Promise<WebhookResponse>;
  sendContactEvent: (data: ContactEventData) => Promise<WebhookResponse>;
  sendBatchEvents: (data: BatchEventData) => Promise<WebhookResponse>;
  sendCustomEvent: (eventName: string, data: any, webhookType?: 'contact' | 'analytics' | 'batch') => Promise<WebhookResponse>;
  isLoading: boolean;
  error: Error | null;
  lastResponse: WebhookResponse | null;
  reset: () => void;
}

// 커스텀 이벤트 데이터
export interface CustomEventData {
  event: string;
  data: Record<string, any>;
  webhookType?: 'contact' | 'analytics' | 'batch';
  timestamp?: string;
}

// 타입 재export
export type {
  AnalyticsEventData,
  ContactEventData,
  BatchEventData,
  WebhookConfig
}; 